import { useCallback, useEffect, useRef, useState } from 'react';
import { VideoPlayingState } from '@yandex-int/ims.stream-player-public-types/build-es5/VideoPlayingState';
import { noop } from 'lodash';
import { useVideoPlayer } from '../../../utils/VideoPlayerProvider';
import { UseGridCardPlayerParams } from './types';

export const useGridCardPlayer = ({
    content,
    containerRef,
    onVideoPlay,
    onVideoStart,
    onVideoEnd,
    onVideoPause,
    onClickInPlayingState,
}: UseGridCardPlayerParams) => {
    const [mediaCoverEnabled, setMediaCoverEnabled] = useState(true);
    const [spinnerEnabled, setSpinnerEnabled] = useState(false);
    const handleUnmountRef = useRef<VoidFunction>();
    const player = useVideoPlayer();

    const handleMouseOn = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            e.stopPropagation();
            e.preventDefault();

            if (
                !player ||
                !containerRef.current ||
                content[0].type !== 'video' ||
                !player.isPlayerEnabled
            ) {
                return noop;
            }

            if (
                player.playerState.playingState === VideoPlayingState.PLAY &&
                player.getActiveNode() === containerRef.current
            ) {
                return onClickInPlayingState?.();
            }

            player.play(containerRef.current);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [content, player, onClickInPlayingState],
    );

    const handleMouseOff = useCallback(() => {
        if (
            !player ||
            !containerRef.current ||
            content[0].type !== 'video' ||
            !player.isPlayerEnabled
        ) {
            return noop;
        }

        player.pause(containerRef.current);
        setSpinnerEnabled(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [player, content]);

    useEffect(() => {
        if (!player || !containerRef.current || content[0].type !== 'video') {
            return noop;
        }

        const firstVideo = content[0].media[0];

        const intersectionCb = () => {
            if (!containerRef.current) {
                return;
            }

            handleUnmountRef.current = player.mount({
                first_frame_url: firstVideo.firstFrameUrl,
                container: containerRef.current,
                streams: firstVideo.streams?.map((stream) => ({
                    stream: stream.stream,
                    stream_type: stream.streamType,
                })),
                onVideoPlay,
                onVideoStart,
                onVideoEnd,
                onVideoPause,
            });
        };

        const unsubscribe = player.onIntersection(containerRef.current, intersectionCb);

        return () => {
            handleUnmountRef.current?.();
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content, containerRef.current]);

    useEffect(() => {
        if (!player || !containerRef.current || content[0].type !== 'video') {
            return noop;
        }

        if (player.getActiveNode() !== containerRef.current) {
            return noop;
        }

        if (player.playerState.playingState === VideoPlayingState.BUFFERING) {
            setSpinnerEnabled(true);
        }

        if (player.playerState.playingState === VideoPlayingState.PLAY) {
            setSpinnerEnabled(false);
            setMediaCoverEnabled(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [player?.playerState.playingState, player?.playerState.startStage]);

    return {
        handleMouseOn,
        handleMouseOff,
        mediaCoverEnabled,
        spinnerEnabled,
    };
};
